import React, {useState} from 'react';
import {Button, EMPHASIS, FlexRow, Icon, Label, Pill, PillVariant, Text, TextArea} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {DELETED_USER, describeActivity, describeReply, isReplyEvent, ReplyCopy} from './quoteActivityCopy';
import {MAX_REPLY_LENGTH, sendQuoteReply} from './sendQuoteReply';
import {
	ReplyBody,
	ReplyBodyOutbound,
	ReplyEntry,
	ReplyThread,
	RoundAvatar,
	Timeline,
	TimelineBody,
	TimelineEntry,
	TimelineMarker,
	TimelineRail,
	Timestamp,
} from './QuoteActivityList.styled';

export type QuoteActivityItem = {
	id: string;
	eventType: string | null;
	/** The quote revision this event belongs to; separates a first publish from a republish. */
	revisionNumber: number | null;
	createdAt: string | null;
	clientContactName: string | null;
	/** The live contact, while one still exists — the reply target. Null once the contact is deleted. */
	clientContact: {id: string} | null;
	reason: string | null;
	person: {id: string; fullName: string | null} | null;
};

type Props = {
	activities: QuoteActivityItem[];
	/** Relay global id of the project's quote — required for the Reply composer to submit. */
	quoteId?: string | null;
	/** Whether the viewer may email a reply (permission and quote status, decided by the section). */
	canReply?: boolean;
	/** Called after a reply is accepted, so the page can refresh anything outside the payload. */
	onReplied?: () => void;
};

/**
 * The rail icon per event, tinted to the outcome: approved reads success, declined error. A
 * revision is neither — it reopens the quote rather than resolving it — so it takes the neutral
 * tint and the pencil that names the editing it hands back to the user.
 *
 * Every event type the log can render needs an entry here. The fallback below keeps an unknown
 * type from crashing, but it silently borrows PUBLISHED's icon, so a missing entry shows the wrong
 * glyph rather than an obvious blank.
 */
const EVENT_ICON: {[eventType: string]: {icon: string; color: string}} = {
	PUBLISHED: {icon: 'note', color: 'primary'},
	SENT: {icon: 'mail', color: 'primary'},
	APPROVED: {icon: 'checkCircle', color: 'success'},
	DECLINED: {icon: 'closeCircle', color: 'error'},
	REVISING: {icon: 'edit', color: 'default'},
};

type ThreadReply = {activity: QuoteActivityItem; copy: ReplyCopy};

/**
 * One timeline event plus the email correspondence that followed it. Replies attach to the most
 * recent timeline event before them, which in practice is the send they answer.
 */
type ActivityGroup = {
	activity: QuoteActivityItem;
	copy: NonNullable<ReturnType<typeof describeActivity>>;
	replies: ThreadReply[];
};

/**
 * The audit log, rendered in the order given — the company service already sorted it newest first
 * (with an id tiebreak, because publish-then-decide can land inside the same second), so this does
 * no sorting of its own and cannot disagree with the server about the order.
 *
 * Timeline events render on the rail; reply events (a client's email, the user's emailed answer)
 * nest as a correspondence thread under the event they follow. The Reply button and its composer
 * live inside the thread, and the reply is addressed to the latest client contact who wrote in it.
 *
 * Presentational: it takes a plain array, not a fragment reference. The Relay boundary is one level
 * up in QuoteActivitySection.
 *
 * The avatar deliberately shows initials rather than the person's picture. Avatar's props are
 * name/src/size only, so a picture URL could not also carry the `data-private` attribute that keeps
 * an authenticated asset URL out of session recordings.
 */
export const QuoteActivityList = ({activities, quoteId, canReply, onReplied}: Props): JSX.Element => {
	const {formatMessage, formatDate} = useIntl();
	// Which group's composer is open, by the group's activity id. One at a time is deliberate:
	// each open composer is an unsent mail, and two at once invites sending the wrong one.
	const [composerFor, setComposerFor] = useState<string | null>(null);
	const [message, setMessage] = useState('');
	const [inFlight, setInFlight] = useState(false);

	// Fixed day-month-year order with a 24h clock, deliberately not the locale's default ordering,
	// so the metadata column reads identically for every viewer.
	const formatStamp = (iso: string) =>
		`${formatDate(iso, {day: '2-digit'})} ${formatDate(iso, {month: 'short'})} ${formatDate(iso, {
			year: 'numeric',
		})} · ${formatDate(iso, {hour: '2-digit', minute: '2-digit', hour12: false})}`;

	// The log reads as a chronology: created at the top, the outcome at the bottom. The server
	// sorts newest-first and that stays, because a paginated `first: N` must return the most recent
	// N rather than the oldest — so the reversal belongs here, at the point of display.
	const oldestFirst = activities.slice().reverse();

	const groups: ActivityGroup[] = [];
	for (const activity of oldestFirst) {
		if (isReplyEvent(activity.eventType)) {
			const copy = describeReply({
				eventType: activity.eventType,
				revisionNumber: activity.revisionNumber,
				personName: activity.person ? activity.person.fullName : null,
				clientContactName: activity.clientContactName,
				reason: activity.reason,
			});
			const parent = groups[groups.length - 1];
			// A reply with no preceding timeline event has nothing to nest under. Within one page
			// of the log that cannot happen — a reply is always written after the send it answers —
			// but a reply whose parent falls outside the pagination window arrives parentless and
			// is not rendered; the row itself survives in the audit table.
			if (copy && parent) {
				parent.replies.push({activity, copy});
			}
			continue;
		}
		const copy = describeActivity({
			eventType: activity.eventType,
			revisionNumber: activity.revisionNumber,
			personName: activity.person ? activity.person.fullName : null,
			clientContactName: activity.clientContactName,
			reason: activity.reason,
		});
		// An unknown event type yields null copy and is skipped, so an older client tolerates an
		// event type a newer backend writes.
		if (copy) {
			groups.push({activity, copy, replies: []});
		}
	}

	if (groups.length === 0) {
		return <Text>{formatMessage({id: 'quote_activity.empty'})}</Text>;
	}

	const closeComposer = () => {
		setComposerFor(null);
		setMessage('');
	};

	const submitReply = (targetContactId: string) => {
		if (!quoteId || !message.trim() || inFlight) {
			return;
		}
		setInFlight(true);
		sendQuoteReply(
			{quoteId, clientContactId: targetContactId, message},
			() => {
				setInFlight(false);
				closeComposer();
				if (onReplied) onReplied();
			},
			() => setInFlight(false)
		);
	};

	const authorName = (copy: ReplyCopy) =>
		copy.name === DELETED_USER ? formatMessage({id: 'quote_activity.unknown_person'}) : copy.name;

	return (
		<Timeline>
			{groups.map(({activity, copy, replies}) => {
				const person =
					copy.person === DELETED_USER ? formatMessage({id: 'quote_activity.unknown_person'}) : copy.person;
				const rail = EVENT_ICON[activity.eventType as string] || EVENT_ICON.PUBLISHED;
				// The reply goes to whoever wrote last from the client's side, while they still
				// exist — a deleted contact has no address, so no Reply is offered on their thread.
				const lastClientReply = replies
					.slice()
					.reverse()
					.find(reply => reply.copy.isClient);
				const replyTargetId =
					lastClientReply && lastClientReply.activity.clientContact
						? lastClientReply.activity.clientContact.id
						: null;
				const composerOpen = composerFor === activity.id;
				return (
					<TimelineEntry key={activity.id} data-cy="quote-activity-entry">
						<TimelineRail>
							<TimelineMarker>
								<Icon icon={rail.icon} size="s" color={rail.color} />
							</TimelineMarker>
						</TimelineRail>
						<TimelineBody>
							<Label>{formatMessage({id: copy.titleMessageId})}</Label>
							<FlexRow gap="s" alignItems="center">
								<RoundAvatar name={person} size="s" />
								<Text>{person}</Text>
								{copy.contact && (
									<>
										<Text size="1" color="auxiliary">
											&middot;
										</Text>
										<Text size="1" color="auxiliary">
											{formatMessage({id: 'quote_activity.contact_prefix'})}
										</Text>
										<Label>{copy.contact}</Label>
									</>
								)}
							</FlexRow>
							{activity.reason && (
								<Text size="1" color="auxiliary">
									{formatMessage({id: 'quote_activity.reason_prefix'}, {reason: activity.reason})}
								</Text>
							)}
							{replies.length > 0 && (
								<ReplyThread data-cy="quote-reply-thread">
									{replies.map(reply => (
										<ReplyEntry key={reply.activity.id} data-cy="quote-reply-entry">
											<FlexRow gap="s" alignItems="center" justifyContent="space-between">
												<FlexRow gap="s" alignItems="center">
													<RoundAvatar name={authorName(reply.copy)} size="s" />
													<Label noWrap>{authorName(reply.copy)}</Label>
													<Pill variant={PillVariant.INFORMATION}>
														{formatMessage({id: reply.copy.tagMessageId})}
													</Pill>
													<Text size="1" color="auxiliary">
														&middot;
													</Text>
													<Text size="1" color="auxiliary" noWrap>
														{formatMessage({id: reply.copy.bylineMessageId})}
													</Text>
												</FlexRow>
												{reply.activity.createdAt && (
													<Timestamp>{formatStamp(reply.activity.createdAt)}</Timestamp>
												)}
											</FlexRow>
											{reply.copy.body &&
												(reply.copy.isClient ? (
													<ReplyBody>
														<Text>{reply.copy.body}</Text>
													</ReplyBody>
												) : (
													<ReplyBodyOutbound>
														<Text>{reply.copy.body}</Text>
													</ReplyBodyOutbound>
												))}
										</ReplyEntry>
									))}
									{canReply && quoteId && replyTargetId && !composerOpen && (
										<FlexRow gap="s" alignItems="center">
											<Button
												emphasis={EMPHASIS.MEDIUM}
												onPress={() => {
													setComposerFor(activity.id);
													setMessage('');
												}}
												data-cy="quote-reply-button"
											>
												<FlexRow gap="s" alignItems="center">
													<Icon icon="arrowLeft" size="s" />
													{formatMessage({id: 'quote_activity.reply'})}
												</FlexRow>
											</Button>
										</FlexRow>
									)}
									{canReply && quoteId && replyTargetId && composerOpen && (
										<>
											<TextArea
												value={message}
												// TextArea's onChange receives the VALUE, not an event.
												onChange={(value: string) => setMessage(value)}
												placeholder={formatMessage({id: 'quote_activity.reply_placeholder'})}
												isOptional={false}
												maxLength={MAX_REPLY_LENGTH}
												// Required by TextArea's props even though the value is already
												// tracked on every change, so there is nothing to commit on blur.
												onBlur={() => undefined}
												data-cy="quote-reply-input"
											/>
											<FlexRow gap="s" alignItems="center" justifyContent="flex-end">
												<Button
													emphasis={EMPHASIS.LOW}
													onPress={closeComposer}
													disabled={inFlight}
													data-cy="quote-reply-cancel"
												>
													{formatMessage({id: 'common.cancel'})}
												</Button>
												<Button
													emphasis={EMPHASIS.HIGH}
													onPress={() => submitReply(replyTargetId)}
													disabled={!message.trim() || inFlight}
													data-cy="quote-reply-send"
												>
													{formatMessage({id: 'common.send'})}
												</Button>
											</FlexRow>
										</>
									)}
								</ReplyThread>
							)}
						</TimelineBody>
						{activity.createdAt && <Timestamp>{formatStamp(activity.createdAt)}</Timestamp>}
					</TimelineEntry>
				);
			})}
		</Timeline>
	);
};
